import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRestaurant } from "../contexts/RestaurantContext";
import { playChatSound, playNewWalkinSound } from "../lib/sounds";
import { sendPushNotification } from "../lib/pushNotifications";
import { GridFour, CalendarBlank, ChartBar, UsersFour, ListNumbers, SignOut, List, X, BuildingOffice, CaretUpDown, Plus, PencilSimple, Trash, GearSix, Database, Wine, SquaresFour, ChartLineUp, UserCircle, DoorOpen, Table, ChatCircleDots, Bell, CurrencyDollar, BookOpen, Calculator, Package, CreditCard } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import ServiceBar from "./ServiceBar";
import api from "../lib/api";

const navItems = [
  { to: "/", icon: GridFour, label: "Floor Plan" },
  { to: "/waitlist", icon: ListNumbers, label: "Waitlist" },
  { to: "/reservations", icon: CalendarBlank, label: "Reservations" },
  { to: "/walk-ins", icon: DoorOpen, label: "Walk-Ins" },
  { to: "/servers", icon: UsersFour, label: "Servers" },
  { to: "/sections", icon: SquaresFour, label: "Sections" },
  { to: "/events", icon: Wine, label: "Events" },
  { to: "/analytics", icon: ChartBar, label: "Analytics" },
  { to: "/chat", icon: ChatCircleDots, label: "Chat" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
];

const serverNavItems = [
  { to: "/my-section", icon: SquaresFour, label: "My Section" },
  { to: "/chat", icon: ChatCircleDots, label: "Chat" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
];

const adminNavItems = [
  { to: "/server-performance", icon: ChartLineUp, label: "Server Stats" },
  { to: "/members", icon: UserCircle, label: "Members" },
  { to: "/sales", icon: CurrencyDollar, label: "Sales" },
  { to: "/accounting", icon: Calculator, label: "Accounting" },
  { to: "/inventory", icon: Package, label: "Inventory" },
  { to: "/end-of-service", icon: ChartBar, label: "Service History" },
  { to: "/data", icon: Database, label: "Data" },
  { to: "/billing", icon: CreditCard, label: "Billing" },
  { to: "/settings", icon: GearSix, label: "Settings" },
  { to: "/walkthrough", icon: BookOpen, label: "Walkthrough" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { restaurants, activeRestaurant, switchRestaurant, createRestaurant, updateRestaurant, deleteRestaurant, isCreator } = useRestaurant();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showRestPicker, setShowRestPicker] = useState(false);
  const [showAddRest, setShowAddRest] = useState(false);
  const [showEditRest, setShowEditRest] = useState(false);
  const [restForm, setRestForm] = useState({ name: "", address: "" });
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/unread-count");
      setUnreadCount(data.count || 0);
    } catch { /* silent */ }
  }, []);

  const fetchNotifUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setNotifUnread(data.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchUnread(); fetchNotifUnread(); }, [fetchUnread, fetchNotifUnread]);
  // Poll unread + listen for WS chat updates
  useEffect(() => {
    const interval = setInterval(fetchUnread, 15000);
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    let ws;
    if (wsUrl) {
      try {
        ws = new WebSocket(`${wsUrl}/api/ws`);
        ws.onmessage = (e) => {
          const msg = JSON.parse(e.data);
          if (msg.type === "chat_message") {
            if (location.pathname === "/chat") {
              api.put("/chat/messages/read").catch(() => {});
            } else {
              fetchUnread();
              playChatSound();
              const cm = msg.message;
              if (cm && cm.sender_id !== user?.id) {
                sendPushNotification(
                  `${cm.sender_name || "Team"}: ${cm.text}`,
                  cm.is_preset ? "Quick update" : "New message",
                  { tag: "chat-" + cm.id, url: "/chat" }
                );
              }
            }
          }
          if (msg.type === "reservation_update") {
            playNewWalkinSound();
            fetchNotifUnread();
          }
          if (msg.type === "notification") {
            fetchNotifUnread();
            const n = msg.notification;
            if (n && !document.hasFocus()) {
              sendPushNotification(
                n.title,
                n.body || "",
                { tag: "notif-" + n.id, url: "/notifications" }
              );
            }
          }
        };
      } catch { /* silent */ }
    }
    return () => { clearInterval(interval); ws?.close(); };
  }, [fetchUnread, fetchNotifUnread, location.pathname, user?.id]);

  // Clear unread when visiting chat page
  useEffect(() => {
    if (location.pathname === "/chat") {
      setUnreadCount(0);
      api.put("/chat/messages/read").catch(() => {});
    }
    if (location.pathname === "/notifications") {
      setNotifUnread(0);
      api.put("/notifications/read").catch(() => {});
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "#F9F9F7" }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={closeSidebar} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-[220px] flex-shrink-0 bg-white border-r border-stone-200 flex flex-col transition-transform duration-200 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`} data-testid="sidebar">
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-base font-semibold tracking-tight" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>
              Dining Floor
            </h1>
            <button onClick={closeSidebar} className="md:hidden p-1 rounded-md hover:bg-stone-100 text-stone-400" data-testid="close-sidebar-btn">
              <X size={20} />
            </button>
          </div>
          {/* Restaurant Selector */}
          <button
            onClick={() => setShowRestPicker(true)}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border border-white/10 hover:bg-stone-100 transition-colors text-left"
            data-testid="restaurant-selector-btn"
          >
            <BuildingOffice size={16} weight="duotone" style={{ color: "#2F4F4F" }} />
            <span className="flex-1 text-sm font-medium text-stone-700 truncate">{activeRestaurant?.name || "Select Restaurant"}</span>
            {restaurants.length > 1 && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{restaurants.length}</span>
            )}
            <CaretUpDown size={14} className="text-stone-300" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {(user?.role === "server" ? serverNavItems : navItems).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/" || item.to === "/my-section"}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon size={20} weight="duotone" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/chat" && unreadCount > 0 && (
                <span className="ml-auto w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid="chat-unread-badge">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
              {item.to === "/notifications" && notifUnread > 0 && (
                <span className="ml-auto w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid="notif-unread-badge">
                  {notifUnread > 9 ? "9+" : notifUnread}
                </span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="my-2 border-t border-stone-200" />
              <p className="text-[9px] uppercase tracking-widest text-stone-300 font-semibold px-4 mb-1">Admin</p>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    `sidebar-nav-item ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon size={20} weight="duotone" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-3 border-t border-stone-200">
          <div className="flex items-center gap-2 px-3 py-2 mb-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white" style={{ backgroundColor: "#2F4F4F" }}>
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-800 truncate">{user?.name || "User"}</p>
              <p className="text-[10px] text-stone-500 uppercase tracking-wider font-mono">{user?.role || "staff"}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-nav-item w-full text-stone-400 hover:text-red-600"
            data-testid="logout-btn"
          >
            <SignOut size={18} weight="duotone" />
            <span>Sign Out</span>
          </button>
          <p className="text-[9px] text-stone-300 text-center mt-3 px-2">Created by Jordan Thomas</p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-stone-200" data-testid="mobile-topbar">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-600" data-testid="open-sidebar-btn">
            <List size={22} />
          </button>
          <h1 className="text-base font-semibold tracking-tight" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>
            Dining Floor
          </h1>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white" style={{ backgroundColor: "#2F4F4F" }}>
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
        </div>

        <ServiceBar />

        <main className="flex-1 overflow-auto pb-16 md:pb-0">
          <Outlet />
          {/* Site Footer */}
          <footer className="border-t border-stone-200 mt-8 py-6 px-6 text-center" data-testid="site-footer">
            <p className="text-sm font-medium text-stone-700" style={{ fontFamily: "Work Sans" }}>Dining Floor Studio</p>
            <p className="text-xs text-stone-500 mt-1">
              <a href="mailto:jordanlthomas2002@gmail.com" className="hover:text-stone-700 transition-colors">jordanlthomas2002@gmail.com</a>
            </p>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs text-stone-400">
              <Link to="/privacy" className="hover:text-stone-600 transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-stone-600 transition-colors">Terms of Service</Link>
            </div>
            <p className="text-[10px] text-stone-400 mt-2">&copy; 2026 Dining Floor Studio. All rights reserved.</p>
          </footer>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-30 flex items-center justify-around px-1 py-1.5 safe-area-bottom" data-testid="mobile-bottom-nav">
          {(user?.role === "server" ? serverNavItems : navItems).map((item) => {
            const isActive = item.to === "/" || item.to === "/my-section" ? location.pathname === item.to : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className="flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg transition-colors min-w-[56px] relative"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon size={20} weight={isActive ? "fill" : "regular"} style={{ color: isActive ? "#2F4F4F" : "#A8A29E" }} />
                {item.to === "/chat" && unreadCount > 0 && (
                  <span className="absolute top-0 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
                {item.to === "/notifications" && notifUnread > 0 && (
                  <span className="absolute top-0 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold">
                    {notifUnread > 9 ? "9+" : notifUnread}
                  </span>
                )}
                <span className={`text-[10px] font-medium ${isActive ? "text-[#2F4F4F]" : "text-stone-400"}`}>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Restaurant Picker Dialog */}
      <Dialog open={showRestPicker} onOpenChange={setShowRestPicker}>
        <DialogContent data-testid="restaurant-picker-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Switch Restaurant</DialogTitle>
            <DialogDescription className="text-xs text-stone-500">Pick a restaurant to manage. Creator accounts can run multiple locations.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto">
            {restaurants.map((r) => (
              <div
                key={r.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${activeRestaurant?.id === r.id ? "border-[#2F4F4F] bg-[#2F4F4F]/5" : "border-stone-200 hover:border-stone-300"}`}
                onClick={() => { switchRestaurant(r); setShowRestPicker(false); }}
                data-testid={`restaurant-option-${r.id}`}
              >
                <BuildingOffice size={20} weight="duotone" style={{ color: activeRestaurant?.id === r.id ? "#2F4F4F" : "#A8A29E" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{r.name}</p>
                  {r.address && <p className="text-[10px] text-stone-500 truncate">{r.address}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); setRestForm({ name: r.name, address: r.address || "" }); setShowEditRest(true); setShowRestPicker(false); }} className="p-1 rounded hover:bg-stone-100" data-testid={`edit-restaurant-${r.id}`}>
                    <PencilSimple size={14} className="text-stone-500" />
                  </button>
                  {restaurants.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); deleteRestaurant(r.id); }} className="p-1 rounded hover:bg-red-50" data-testid={`delete-restaurant-${r.id}`}>
                      <Trash size={14} className="text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isCreator ? (
            <Button variant="outline" className="w-full mt-2" onClick={() => { setRestForm({ name: "", address: "" }); setShowAddRest(true); setShowRestPicker(false); }} data-testid="add-restaurant-btn">
              <Plus size={16} className="mr-1.5" /> Add Restaurant
            </Button>
          ) : (
            <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-800 mb-1">Multi-location</p>
              <p className="text-xs text-stone-700 mb-2 leading-snug">Run multiple restaurants from one account with the Enterprise plan.</p>
              <Button variant="outline" size="sm" className="w-full bg-white text-xs h-8" onClick={() => { setShowRestPicker(false); navigate("/billing"); }} data-testid="upgrade-multi-location-btn">
                Upgrade to Enterprise
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Restaurant Dialog */}
      <Dialog open={showAddRest} onOpenChange={setShowAddRest}>
        <DialogContent data-testid="add-restaurant-dialog">
          <DialogHeader><DialogTitle>New Restaurant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name</Label>
              <Input value={restForm.name} onChange={(e) => setRestForm({ ...restForm, name: e.target.value })} placeholder="Restaurant name" className="mt-1.5" data-testid="restaurant-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Address</Label>
              <Input value={restForm.address} onChange={(e) => setRestForm({ ...restForm, address: e.target.value })} placeholder="123 Main St" className="mt-1.5" data-testid="restaurant-address-input" />
            </div>
            <Button onClick={async () => { if (!restForm.name.trim()) return; await createRestaurant(restForm.name, restForm.address); setShowAddRest(false); }} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-restaurant-btn">Create Restaurant</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Restaurant Dialog */}
      <Dialog open={showEditRest} onOpenChange={setShowEditRest}>
        <DialogContent data-testid="edit-restaurant-dialog">
          <DialogHeader><DialogTitle>Edit Restaurant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name</Label>
              <Input value={restForm.name} onChange={(e) => setRestForm({ ...restForm, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Address</Label>
              <Input value={restForm.address} onChange={(e) => setRestForm({ ...restForm, address: e.target.value })} className="mt-1.5" />
            </div>
            <Button onClick={async () => { if (!activeRestaurant) return; await updateRestaurant(activeRestaurant.id, restForm); setShowEditRest(false); }} style={{ backgroundColor: "#2F4F4F" }} className="w-full">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
